import { Controller } from "@hotwired/stimulus"
import { Html5Qrcode } from 'html5-qrcode'
import * as bootstrap from 'bootstrap'
import { escapePickupOrderForHTML } from '../modules/order_html_sanitizer.mjs'

// Connects to data-controller="pickup"
export default class extends Controller {
  static targets = ["scanButton", "statusCamera", "input", "modal", "modalBody", "modalTitle", "deliverButton", "message", "idle", "history", "historyList"]
  static values = { storeId: Number }

  connect() {
    this.html5QrCode = null
    this.currentStream = null
    this.currentOrderId = null
    this.bsModal = new bootstrap.Modal(this.modalTarget)

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.stopScanner()
    })

    document.addEventListener("turbo:before-cache", () => {
      this.stopScanner()
    })
  }

  disconnect() {
    this.stopScanner()
  }

  // ========================================
  // Search
  // ========================================

  search(event) {
    if (event) event.preventDefault()
    const query = this.inputTarget.value.trim()
    if (!query) return

    this.fetchOrder({ order_number: query })
  }

  fetchOrder(params) {
    const url = new URL(`/stores/${this.storeIdValue}/pickup/search`, window.location.origin)
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v))

    fetch(url, {
      headers: { 'Accept': 'application/json' }
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          this.showOrderModal(data.order)
          this.hideMessage()
        } else {
          this.showMessage(data.error, false)
        }
      })
      .catch(error => {
        console.error("Pickup search error:", error)
        this.showMessage("Error de conexión", false)
      })
  }

  // ========================================
  // Modal
  // ========================================

  showOrderModal(order) {
    this.currentOrderId = order.id
    this.currentOrder = order

    // Title usa textContent, por lo que recibe el valor original como texto.
    this.modalTitleTarget.textContent = ` #${order.order_number}`
    order = escapePickupOrderForHTML(order)

    // Items — renderizado estilo cocina con checks verdes
    const itemsHtml = order.order_items.map(item => {
      let ingredientsHtml = ''
      if (item.ingredients && item.ingredients.length > 0) {
        ingredientsHtml = '<div class="pickup-item-ingredients">'
        item.ingredients.forEach(comp => {
          const showHeader = comp.component && comp.component.length > 0
          if (showHeader) {
            ingredientsHtml += `<strong class="small color-green">${comp.component} #${(comp.instance || 0) + 1}:</strong>`
          }
          if (comp.categories) {
            comp.categories.forEach(cat => {
              const indent = showHeader ? 'ms-2' : ''
              ingredientsHtml += `<div class="mb-1 ${indent}"><strong class="small color-green">${cat.category}:</strong><div class="ingredients-compact">`
              cat.ingredients.forEach(ing => {
                const label = ing.quantity > 1 ? `${ing.name} ×${ing.quantity}` : ing.name
                const price = ing.price > 0 ? ` ($${ing.price})` : ''
                ingredientsHtml += `<span class="ingredient-tag small"><i class="bi bi-check-circle-fill color-green me-1"></i>${label}${price}</span>`
              })
              ingredientsHtml += '</div></div>'
            })
          }
        })
        ingredientsHtml += '</div>'
      }

      return `
        <div class="pickup-item">
          <div class="pickup-item-header">
            <span class="pickup-item-qty">${item.quantity}x</span>
            <span class="pickup-item-name">${this.capitalize(item.product_name)}</span>
          </div>
          ${ingredientsHtml}
        </div>`
    }).join('')

    this.modalBodyTarget.innerHTML = `
      <div class="pickup-order-meta">
        <span><i class="bi bi-person-circle"></i> ${order.client_name || ''} ${order.client_last_name || ''}</span>
        ${order.paid_at ? `<span><i class="bi bi-clock"></i> ${order.paid_at}</span>` : ''}
      </div>

      <div class="pickup-order-items">
        ${itemsHtml}
      </div>

      <div class="pickup-order-total">
        <span>Total</span>
        <span>$${order.total.toLocaleString()}</span>
      </div>
    `

    // Reset deliver button
    this.deliverButtonTarget.disabled = false
    this.deliverButtonTarget.innerHTML = '<i class="bi bi-check-circle me-2"></i> Marcar como entregada'

    this.bsModal.show()
  }

  // ========================================
  // Deliver
  // ========================================

  deliver() {
    if (!this.currentOrderId) return

    this.deliverButtonTarget.disabled = true
    this.deliverButtonTarget.innerHTML = '<i class="bi bi-hourglass-split me-2"></i> Procesando...'

    fetch(`/stores/${this.storeIdValue}/pickup/deliver`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ order_id: this.currentOrderId })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          this.bsModal.hide()
          this.addToHistory(this.currentOrder)
          this.inputTarget.value = ''
          this.currentOrderId = null
          this.currentOrder = null
          this.showMessage("Orden entregada con éxito", true)
          this.playSound()
        } else {
          this.showMessage(data.error, false)
          this.deliverButtonTarget.disabled = false
          this.deliverButtonTarget.innerHTML = '<i class="bi bi-check-circle me-2"></i> Marcar como entregada'
        }
      })
      .catch(error => {
        console.error("Pickup deliver error:", error)
        this.showMessage("Error de conexión", false)
        this.deliverButtonTarget.disabled = false
        this.deliverButtonTarget.innerHTML = '<i class="bi bi-check-circle me-2"></i> Marcar como entregada'
      })
  }

  // ========================================
  // QR Scanner
  // ========================================

  scan() {
    const readerEl = document.getElementById("qr-reader-pickup")
    const containerWidth = readerEl ? readerEl.clientWidth : window.innerWidth
    const qrboxSide = Math.floor(containerWidth * 0.75)
    const qrboxSize = { width: qrboxSide, height: qrboxSide }

    if (!this.html5QrCode) {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        const hasCamera = devices.some(device => device.kind === 'videoinput')
        if (hasCamera) {
          this.startScanner(qrboxSize)
        } else {
          this.requestCameraPermission(qrboxSize)
        }
      })
    } else {
      this.stopScanner()
    }
  }

  requestCameraPermission(qrboxSize) {
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        stream.getTracks().forEach(track => track.stop())
        this.startScanner(qrboxSize)
      })
      .catch(() => {
        this.showMessage("No se encontró cámara disponible", false)
      })
  }

  startScanner(qrboxSize) {
    this.html5QrCode = new Html5Qrcode("qr-reader-pickup")
    this.html5QrCode.start(
      { facingMode: "environment" },
      { fps: 10, qrbox: qrboxSize },
      (decodedText) => {
        this.onScanSuccess(decodedText)
        this.stopScanner()
      },
      () => {}
    ).then(() => {
      this.scanButtonTarget.innerHTML = '<i class="bi bi-stop-circle"></i> Detener Escáner'
    }).catch(err => {
      console.error("Error starting scanner", err)
      this.showMessage("No se pudo iniciar la cámara", false)
      this.html5QrCode = null
    })
  }

  stopScanner() {
    if (this.html5QrCode) {
      const scanner = this.html5QrCode
      this.html5QrCode = null
      this.scanButtonTarget.innerHTML = '<i class="bi bi-qr-code-scan"></i> Escanear QR'

      const cleanup = () => {
        try { scanner.clear() } catch (_e) { /* element already removed */ }
        if (this.currentStream) {
          this.currentStream.getTracks().forEach(track => track.stop())
          this.currentStream = null
        }
      }

      if (scanner.isScanning) {
        scanner.stop().then(cleanup).catch(() => cleanup())
      } else {
        cleanup()
      }
    }
  }

  onScanSuccess(decodedText) {
    let qrData
    try {
      qrData = JSON.parse(decodedText)
    } catch (e) {
      console.error("Error parsing QR:", e)
      this.showMessage("QR inválido", false)
      return
    }

    if (typeof qrData.token !== "string" || !qrData.token.trim()) {
      this.showMessage("QR inválido", false)
      return
    }

    this.fetchOrder({ qr_token: qrData.token })
  }

  // ========================================
  // History
  // ========================================

  addToHistory(order) {
    order = escapePickupOrderForHTML(order)

    const now = new Date()
    const time = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`

    const itemsHtml = order.order_items.map(item => {
      let ingredientsHtml = ''
      if (item.ingredients && item.ingredients.length > 0) {
        ingredientsHtml = '<div class="pickup-item-ingredients">'
        item.ingredients.forEach(comp => {
          const showHeader = comp.component && comp.component.length > 0
          if (showHeader) {
            ingredientsHtml += `<strong class="small color-green">${comp.component} #${(comp.instance || 0) + 1}:</strong>`
          }
          if (comp.categories) {
            comp.categories.forEach(cat => {
              const indent = showHeader ? 'ms-2' : ''
              ingredientsHtml += `<div class="mb-1 ${indent}"><strong class="small color-green">${cat.category}:</strong><div class="ingredients-compact">`
              cat.ingredients.forEach(ing => {
                const label = ing.quantity > 1 ? `${ing.name} ×${ing.quantity}` : ing.name
                const price = ing.price > 0 ? ` ($${ing.price})` : ''
                ingredientsHtml += `<span class="ingredient-tag small"><i class="bi bi-check-circle-fill color-green me-1"></i>${label}${price}</span>`
              })
              ingredientsHtml += '</div></div>'
            })
          }
        })
        ingredientsHtml += '</div>'
      }

      return `
        <div class="pickup-history-item">
          <div class="pickup-item-header">
            <span class="pickup-item-qty">${item.quantity}x</span>
            <span class="pickup-item-name">${this.capitalize(item.product_name)}</span>
          </div>
          ${ingredientsHtml}
        </div>`
    }).join('')

    const card = document.createElement('div')
    card.className = 'pickup-history-card pickup-history-card-new'
    card.innerHTML = `
      <div class="pickup-history-card-summary" data-action="click->pickup#toggleDetail">
        <div class="pickup-history-card-header">
          <span class="fw-semibold">#${order.order_number}</span>
          <span class="text-muted small">${time}</span>
        </div>
        <div class="pickup-history-card-body">
          <span class="small text-muted">
            <i class="bi bi-person-circle me-1"></i>${order.client_name || ''} ${order.client_last_name || ''}
          </span>
          <div class="d-flex align-items-center gap-2">
            <span class="small fw-semibold color-green">$${order.total.toLocaleString()}</span>
            <i class="bi bi-chevron-down pickup-history-chevron"></i>
          </div>
        </div>
      </div>
      <div class="pickup-history-detail" style="display: none;">
        ${itemsHtml}
      </div>
    `

    const list = this.historyListTarget
    list.prepend(card)

    // Mantener máximo 10
    while (list.children.length > 10) {
      list.removeChild(list.lastElementChild)
    }

    // Mostrar sección si estaba oculta
    this.historyTarget.style.display = ''
  }

  toggleDetail(event) {
    const summary = event.currentTarget
    const card = summary.closest('.pickup-history-card')
    const detail = card.querySelector('.pickup-history-detail')
    const chevron = summary.querySelector('.pickup-history-chevron')

    if (detail.style.display === 'none') {
      detail.style.display = ''
      chevron.classList.remove('bi-chevron-down')
      chevron.classList.add('bi-chevron-up')
    } else {
      detail.style.display = 'none'
      chevron.classList.remove('bi-chevron-up')
      chevron.classList.add('bi-chevron-down')
    }
  }

  // ========================================
  // UI Helpers
  // ========================================

  showMessage(text, success) {
    const el = this.messageTarget
    el.classList.remove('d-none', 'alert-success', 'alert-danger')
    el.classList.add(success ? 'alert-success' : 'alert-danger')
    const icon = document.createElement('i')
    icon.className = `bi ${success ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'} me-2`
    el.replaceChildren(icon, document.createTextNode(String(text || '')))

    clearTimeout(this._messageTimeout)
    this._messageTimeout = setTimeout(() => {
      el.classList.add('d-none')
    }, 5000)
  }

  hideMessage() {
    this.messageTarget.classList.add('d-none')
  }

  playSound() {
    try {
      const audio = new Audio('/sounds/bell_paid_validator.wav')
      audio.play()
    } catch (_e) { /* sound not critical */ }
  }

  capitalize(str) {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
  }
}
